// 行の除外機能の管理

import { PrimaryDataSet, ExcludedRow, ColumnId, DataValue } from '../../types';
import { ROW_ID_COLUMN_ID } from '../../types/system-columns';
import logger from './logger';

/**
 * 指定された行を除外する
 * @param dataset 対象のデータセット
 * @param rowIndices 除外する行のインデックス（現在のdata内でのインデックス）
 * @param comment 除外理由（オプション）
 */
export const excludeRows = (
  dataset: PrimaryDataSet, 
  rowIndices: readonly number[], 
  comment?: string
): void => {
  const startTime = Date.now();
  
  // 前提条件チェック
  logger.assert(dataset.type === 'primary', 'Only primary datasets support exclusion');
  logger.assert(rowIndices.length > 0, 'Must specify at least one row to exclude');
  
  // RowNumber列が存在することを確認
  const rowNumberValues = dataset.data[ROW_ID_COLUMN_ID];
  if (!rowNumberValues) {
    throw new Error('Dataset does not have RowNumber column');
  }
  
  // 除外管理を初期化（必要な場合）
  if (!dataset.exclusions) {
    dataset.exclusions = {
      excludedRows: {}
    };
  }
  
  // インデックスを降順でソート（後ろから削除するため）
  const sortedIndices = [...rowIndices].sort((a, b) => b - a);
  
  // 除外するデータを収集
  const excludedData: Array<{ rowNumber: number; data: Record<ColumnId, DataValue> }> = [];
  
  for (const index of sortedIndices) {
    if (index < 0 || index >= dataset.rowCount) {
      logger.warn('[RowExclusion] Invalid row index', { index, rowCount: dataset.rowCount });
      continue;
    }
    
    // RowNumber値を取得
    const rowNumber = rowNumberValues[index] as number;
    
    // 行データを収集（列指向→行指向）
    const rowData: Record<ColumnId, DataValue> = {};
    for (const [columnId, values] of Object.entries(dataset.data)) {
      rowData[columnId as ColumnId] = values[index];
    }
    
    excludedData.push({ rowNumber, data: rowData });
  }
  
  // 除外データをRecordに追加
  const excludedAt = new Date().toISOString();
  for (const { rowNumber, data } of excludedData) {
    const excludedRow: ExcludedRow = {
      data,
      comment,
      excludedAt
    };
    dataset.exclusions.excludedRows[rowNumber] = excludedRow;
  }
  
  // dataから削除（後ろから削除）
  for (const index of sortedIndices) {
    for (const columnId of Object.keys(dataset.data) as ColumnId[]) {
      dataset.data[columnId].splice(index, 1);
    }
  }
  
  // スキーマの行数を更新
  dataset.rowCount -= sortedIndices.length;
  
  logger.info('[RowExclusion] Rows excluded', {
    datasetId: dataset.id,
    excludedCount: sortedIndices.length,
    remainingRows: dataset.rowCount,
    comment,
    duration: Date.now() - startTime
  });
};

/**
 * 除外された行を復元する
 * @param dataset 対象のデータセット
 * @param rowNumbers 復元する行のRowNumber値
 */
export const restoreRows = (
  dataset: PrimaryDataSet,
  rowNumbers: readonly number[]
): void => {
  const startTime = Date.now();
  
  // 前提条件チェック
  logger.assert(dataset.type === 'primary', 'Only primary datasets support exclusion');
  logger.assert(rowNumbers.length > 0, 'Must specify at least one row to restore');
  
  if (!dataset.exclusions || Object.keys(dataset.exclusions.excludedRows).length === 0) {
    logger.warn('[RowExclusion] No excluded rows to restore');
    return;
  }
  
  // 復元するデータを収集
  const rowsToRestore: Array<{ rowNumber: number; data: Record<ColumnId, DataValue> }> = [];
  
  for (const rowNumber of rowNumbers) {
    const excludedRow = dataset.exclusions.excludedRows[rowNumber];
    if (!excludedRow) {
      logger.warn('[RowExclusion] Row not found in excluded rows', { rowNumber });
      continue;
    }

    rowsToRestore.push({
      rowNumber,
      data: excludedRow.data
    });

    // 除外リストから削除
    delete dataset.exclusions.excludedRows[rowNumber];
  }
  
  if (rowsToRestore.length === 0) {
    logger.warn('[RowExclusion] No valid rows to restore');
    return;
  }
  
  // RowNumber順でソート
  rowsToRestore.sort((a, b) => a.rowNumber - b.rowNumber);
  
  // 現在のデータと復元データをマージする
  const currentRowNumbers = dataset.data[ROW_ID_COLUMN_ID] as number[];
  
  // すべての列について新しい配列を作成
  const newData: Record<ColumnId, DataValue[]> = {};
  for (const columnId of Object.keys(dataset.data) as ColumnId[]) {
    newData[columnId] = [];
  }
  
  // マージ処理
  let currentIndex = 0;
  let restoreIndex = 0;
  
  while (currentIndex < currentRowNumbers.length || restoreIndex < rowsToRestore.length) {
    const currentRowNumber = currentIndex < currentRowNumbers.length ? currentRowNumbers[currentIndex] : Infinity;
    const restoreRowNumber = restoreIndex < rowsToRestore.length ? rowsToRestore[restoreIndex].rowNumber : Infinity;
    
    if (restoreRowNumber < currentRowNumber) {
      // 復元データを追加
      const { data } = rowsToRestore[restoreIndex];
      for (const columnId of Object.keys(newData) as ColumnId[]) {
        newData[columnId].push(data[columnId]);
      }
      restoreIndex++;
    } else {
      // 現在のデータを追加
      for (const columnId of Object.keys(newData) as ColumnId[]) {
        newData[columnId].push(dataset.data[columnId][currentIndex]);
      }
      currentIndex++;
    }
  }
  
  // データを置き換え
  dataset.data = newData;
  
  // スキーマの行数を更新
  dataset.rowCount += rowsToRestore.length;
  
  logger.info('[RowExclusion] Rows restored', {
    datasetId: dataset.id,
    restoredCount: rowsToRestore.length,
    totalRows: dataset.rowCount,
    duration: Date.now() - startTime
  });
};

/**
 * すべての除外を解除する
 * @param dataset 対象のデータセット
 */
export const clearAllExclusions = (dataset: PrimaryDataSet): void => {
  if (!dataset.exclusions || Object.keys(dataset.exclusions.excludedRows).length === 0) {
    logger.info('[RowExclusion] No excluded rows to clear');
    return;
  }

  const allRowNumbers = Object.keys(dataset.exclusions.excludedRows).map(Number);
  restoreRows(dataset, allRowNumbers);
  
  // 除外管理を削除
  delete dataset.exclusions;
  
  logger.info('[RowExclusion] All exclusions cleared', {
    datasetId: dataset.id,
    clearedCount: allRowNumbers.length
  });
};

/**
 * 除外された行の数を取得
 * @param dataset 対象のデータセット
 */
export const getExcludedRowCount = (dataset: PrimaryDataSet): number => {
  return dataset.exclusions?.excludedRows ? Object.keys(dataset.exclusions.excludedRows).length : 0;
};

/**
 * 除外された行のRowNumber一覧を取得
 * @param dataset 対象のデータセット
 */
export const getExcludedRowNumbers = (dataset: PrimaryDataSet): number[] => {
  if (!dataset.exclusions) {
    return [];
  }
  return Object.keys(dataset.exclusions.excludedRows).map(Number);
};

/**
 * 行にコメントを追加する
 * @param dataset 対象のデータセット
 * @param rowNumber 行番号
 * @param comment コメント内容
 */
export const addComment = (dataset: PrimaryDataSet, rowNumber: number, comment: string): void => {
  if (!dataset.comments) {
    dataset.comments = {};
  }
  
  dataset.comments[rowNumber] = {
    comment,
    commentedAt: new Date().toISOString()
  };
  
  logger.info('[RowExclusion] Comment added', {
    datasetId: dataset.id,
    rowNumber,
    comment
  });
};

/**
 * 行のコメントを更新する
 * @param dataset 対象のデータセット
 * @param rowNumber 行番号
 * @param comment 新しいコメント内容
 */
export const updateComment = (dataset: PrimaryDataSet, rowNumber: number, comment: string): void => {
  if (!dataset.comments) {
    dataset.comments = {};
  }
  
  dataset.comments[rowNumber] = {
    comment,
    commentedAt: new Date().toISOString()
  };
  
  logger.info('[RowExclusion] Comment updated', {
    datasetId: dataset.id,
    rowNumber,
    comment
  });
};

/**
 * 行のコメントを削除する
 * @param dataset 対象のデータセット
 * @param rowNumber 行番号
 */
export const removeComment = (dataset: PrimaryDataSet, rowNumber: number): void => {
  if (!dataset.comments) {
    return;
  }
  
  delete dataset.comments[rowNumber];

  if (Object.keys(dataset.comments).length === 0) {
    delete dataset.comments;
  }
  
  logger.info('[RowExclusion] Comment removed', {
    datasetId: dataset.id,
    rowNumber
  });
};